(ns ui.view.transaction-process.components.enrich-resume
  "The review of a finished AI enrichment job, mounted once at the app root.

   Jobs run in the background and finish wherever the user is; the top bar's
   process list (ui.view.enrich.processes) offers each finished job for review.
   Reviewing opens what fits the job:
   - an existing entity: the old-vs-new comparison of ui.enrich.existing
   - a transaction process: its create modal pre-filled from the result
   - a configurable entity: its creation curtain pre-filled from the result

   Closing a review keeps the job in the list; saving or discarding resolves it."
  (:require
   [frontend.view.entity :as entity]
   [re-frame.core :as rf]
   [reagent.core :as r]
   [ui.enrich.apply :as enrich-apply]
   [ui.enrich.event :as enrich-event]
   [ui.enrich.existing :as enrich-existing]
   [ui.enrich.model :as enrich-model]
   [ui.enrich.prefill :as prefill]
   [ui.enrich.sub :as enrich-sub]
   [ui.state.session.sub :as session-sub]
   [ui.view.transaction-process.create-modal :as create-modal]))

(defn- open-enriched-creator
  "Open the configurable entity's creation curtain, pre-filled with what the
   user had entered and the job's suggestions over it, and end the review: the
   curtain is on its own from here. Simple entities persist these on create, so
   no post-create apply. The create goes through the job, which records the
   entity and is resolved in the same transaction; closing the curtain without
   a create keeps the job in the process list."
  [{:keys [job]}]
  (r/create-class
   {:component-did-mount
    (fn [_]
      (let [tenant-schema @(rf/subscribe [::session-sub/tenant-schema])]
        (entity/open-creator!
         (-> job :request-context :entity-type)
         {:initial-values       (enrich-apply/creator-initial-values job tenant-schema)
          ;; Creates through the job, which is resolved with the entity.
          :enrichment-result-id (:result-id job)
          :on-created!          (fn [_] (rf/dispatch [::enrich-event/resolve (:result-id job)]))}))
      (rf/dispatch [::enrich-event/close-review]))
    :reagent-render (fn [_] nil)}))

(defn view
  "Renders the review of the job under review, once its suggestions are loaded."
  []
  (let [job      @(rf/subscribe [::enrich-sub/reviewed-job])
        close!   #(rf/dispatch [::enrich-event/close-review])
        resolve! #(rf/dispatch [::enrich-event/resolve (:result-id job)])]
    (when job
      (case (enrich-model/job-kind job)
        :existing
        ^{:key (:result-id job)}
        [enrich-existing/review-curtain
         {:result      job
          :on-close    close!
          :on-resolved resolve!}]

        ;; A job that already created its process is applied onto that one
        ;; again (the process list offers it), never created twice.
        :tp-create
        (when-not (:target-id job)
          [create-modal/view
           {:initial-values       (prefill/create-modal-values job)
            :suppress-enrich?     true
            :enrichment-result-id (:result-id job)
            :on-close             close!
            ;; The create modal only persists its own few fields; once the process
            ;; exists, apply everything else the AI extracted onto it and its slot
            ;; entities, and resolve the job when all of it is saved. `job` is
            ;; captured here because on-close ends the review before on-created
            ;; fires.
            :on-created           (fn [new-id]
                                    (rf/dispatch [::enrich-apply/created new-id job]))}])

        :config-create
        ^{:key (:result-id job)}
        [open-enriched-creator {:job job}]

        ;; Survey jobs are applied by the survey editor, never reviewed here.
        nil))))
